import {
  $isListItemNode,
  ListItemNode,
  ListNode,
  type ListType,
  type SerializedListNode,
} from '@lexical/list';
import type {
  DOMConversionFn,
  DOMConversionMap,
  EditorConfig,
  LexicalNode,
  Spread,
} from 'lexical';

import { $createCustomListItemNode } from './CustomListItemNode';

export type SerializedCustomListNode = Spread<
  {
    id: string;
  },
  SerializedListNode
>;

/**
 * Valid text direction values
 */
type TextDirection = 'ltr' | 'rtl';

/**
 * Extracts and validates the text direction from an HTML element
 * @returns The direction if valid ('ltr' or 'rtl'), null otherwise
 */
function extractDirectionFromElement(
  element: HTMLElement,
): TextDirection | null {
  const dir = element.getAttribute('dir');
  return dir === 'ltr' || dir === 'rtl' ? dir : null;
}

export class CustomListNode extends ListNode {
  __id: string = '';

  static getType(): string {
    return 'custom-list';
  }

  setId(id: string): void {
    const self = this.getWritable();
    self.__id = id;
  }

  getId(): string {
    const self = this.getLatest();
    return self.__id;
  }

  static clone(node: CustomListNode): CustomListNode {
    const newNode = new CustomListNode(
      node.__listType,
      node.__start,
      node.__key,
    );
    newNode.__id = node.__id;
    newNode.__dir = node.__dir;
    return newNode;
  }

  createDOM(config: EditorConfig): HTMLElement {
    const dom = super.createDOM(config);

    if (this.__id) {
      dom.setAttribute('id', this.__id);
    }

    if (this.__dir) {
      dom.setAttribute('dir', this.__dir);
    }

    return dom;
  }

  updateDOM(
    prevNode: ListNode,
    dom: HTMLElement,
    config: EditorConfig,
  ): boolean {
    const baseUpdated = super.updateDOM(prevNode as this, dom, config);
    const prevCustomNode = prevNode as CustomListNode;

    // Check if direction changed (only if it was previously set)
    const dirChanged =
      prevCustomNode.__dir !== undefined && prevCustomNode.__dir !== this.__dir;

    return baseUpdated || dirChanged;
  }

  exportJSON(): SerializedCustomListNode {
    return {
      ...super.exportJSON(),
      type: 'custom-list',
      version: 1,
      id: this.__id,
    };
  }

  static importJSON(serializedNode: SerializedCustomListNode): CustomListNode {
    const node = $createCustomListNode(serializedNode.listType);
    node.setId(serializedNode.id);
    node.setDirection(serializedNode.direction);
    return node;
  }

  static importDOM(): DOMConversionMap | null {
    // @ts-expect-error - accessing protected method
    const converter = super.importDOM()?.ol?.()?.conversion as
      | DOMConversionFn<HTMLElement>
      | undefined;

    if (!converter) {
      return null;
    }

    const customConvertFunction = (element: HTMLElement) => {
      const baseResult = converter(element);

      if (!baseResult?.node) {
        return baseResult;
      }

      const node = baseResult.node as CustomListNode;

      // Extract and set ID if present
      if (element.id) {
        node.setId(element.id);
      }

      // Extract and set direction if present
      const direction = extractDirectionFromElement(element);
      if (direction) {
        node.setDirection(direction);
      }

      // Set normalization function for nested lists
      baseResult.after = $normalizeChildren;

      return baseResult;
    };

    return {
      ol: () => ({
        conversion: customConvertFunction,
        priority: 0,
      }),
      ul: () => ({
        conversion: customConvertFunction,
        priority: 0,
      }),
    };
  }
}

/**
 * Custom normalization function to allow nested lists within list item elements.
 *
 * Wraps non-list-item nodes in a CustomListItemNode to ensure proper list structure.
 *
 * @see https://github.com/facebook/lexical/blob/6e10210fd1e113ccfafdc999b1d896733c5c5bea/packages/lexical-list/src/LexicalListNode.ts#L284-L303
 * Original implementation from Lexical (MIT license)
 * Copyright (c) Meta Platforms, Inc. and affiliates.
 */
function $normalizeChildren(nodes: Array<LexicalNode>): Array<ListItemNode> {
  return nodes.map((node) =>
    $isListItemNode(node) ? node : $wrapInListItem(node),
  );
}

/**
 * Wraps a node in a CustomListItemNode to ensure it can be part of a list structure
 */
function $wrapInListItem(node: LexicalNode): ListItemNode {
  const listItemWrapper = $createCustomListItemNode();
  return listItemWrapper.append(node);
}

export function $createCustomListNode(type: ListType): CustomListNode {
  return new CustomListNode(type, 1);
}

export function $isCustomListNode(
  node: LexicalNode | null | undefined,
): node is CustomListNode {
  return node instanceof CustomListNode;
}
